import { Injectable, inject } from '@angular/core';

import { SortDirection } from '@ninety/platform/models/enums/sort-direction';
import { IssueSortField, IssueSortFieldEnum } from '@ninety/platform/models/issues/issues-sort-field';
import { LocalStorageService } from '@ninety/platform/services';

@Injectable({
  providedIn: 'root',
})
export class IssuesLocalStorageService {
  private readonly localStorageService = inject(LocalStorageService);

  /**
   * Gets the localStorage key for sort settings.
   */
  getSortKey(isShortTerm: boolean): string {
    return `issues.sort.${isShortTerm ? 'shortTerm' : 'longTerm'}`;
  }

  /**
   * Gets the localStorage key for pagination settings.
   */
  getPaginationKey(isShortTerm: boolean): string {
    return `issues.${isShortTerm ? 'shortTerm' : 'longTerm'}.pagination.size`;
  }

  /**
   * Retrieves and validates sort settings from localStorage.
   * Returns null if the stored value is invalid or missing.
   */
  getSort(key: string): { field: IssueSortField; direction: SortDirection } | null {
    const sortValue = this.localStorageService.get(key);
    if (!sortValue) {
      return null;
    }

    const parsed = this.parseSortValue(sortValue, key);
    if (!parsed) {
      return null;
    }

    return this.validateSort(parsed);
  }

  /**
   * Saves sort settings to localStorage.
   */
  saveSort(key: string, field: IssueSortField, direction: SortDirection): void {
    if (field === 'None') {
      this.localStorageService.delete(key);
    } else {
      this.localStorageService.set(key, { field, direction });
    }
  }

  /**
   * Saves pagination page size to localStorage.
   */
  savePaginationSize(key: string, pageSize: number): void {
    this.localStorageService.set(key, pageSize);
  }

  /**
   * Parses a JSON string into a sort value object.
   * Removes corrupted data from localStorage if parsing fails.
   */
  private parseSortValue(sortValue: string, key: string): { field: string; direction: string } | null {
    try {
      return JSON.parse(sortValue) as { field: string; direction: string };
    } catch {
      // Invalid data - remove it from localStorage to prevent future errors
      this.localStorageService.delete(key);
      return null;
    }
  }

  /**
   * Validates that a parsed sort object has valid field and direction values.
   * Returns null if validation fails, otherwise returns the validated sort object.
   */
  private validateSort(parsed: {
    field: string;
    direction: string;
  }): { field: IssueSortField; direction: SortDirection } | null {
    const isValidField = (field: string): field is IssueSortField => {
      return Object.values(IssueSortFieldEnum).includes(field as IssueSortFieldEnum);
    };

    const isValidDirection = (direction: string): direction is SortDirection => {
      return direction === SortDirection.ASC || direction === SortDirection.DESC;
    };

    if (!isValidField(parsed.field) || !isValidDirection(parsed.direction)) {
      return null;
    }

    return {
      field: parsed.field,
      direction: parsed.direction,
    };
  }

  deleteSort(key: string): void {
    this.localStorageService.delete(key);
  }
}
